import type {
  ResourceKindInfo,
  TopicResource,
  TopicResourcesResponse,
  WorkspaceResourcesResponse,
} from '../types/api'
import { api } from './client'

/**
 * The owning scope of a resource collection: a topic or a workspace. Every
 * resource operation is identical apart from the URL prefix, so the former
 * `resourcesApi` / `workspaceResourcesApi` pair is replaced by a single
 * scope-parametrized helper.
 */
export type ResourceScope =
  | { kind: 'topic'; id: string }
  | { kind: 'workspace'; id: string }

function collectionUrl(scope: ResourceScope): string {
  return scope.kind === 'topic'
    ? `/api/topics/${scope.id}/resources`
    : `/api/workspaces/${scope.id}/resources`
}

/**
 * CRUD + resource-type operations for the resources owned by `scope`. Replaces
 * the duplicated `resourcesApi` / `workspaceResourcesApi` objects.
 */
export function resourcesFor(scope: ResourceScope) {
  const base = collectionUrl(scope)
  const one = (resId: string) => `${base}/${resId}`
  return {
    list: (): Promise<TopicResourcesResponse | WorkspaceResourcesResponse> =>
      scope.kind === 'topic'
        ? api.get<TopicResourcesResponse>(base)
        : api.get<WorkspaceResourcesResponse>(base),
    get: (resId: string): Promise<TopicResource> =>
      api.get<TopicResource>(one(resId)),
    create: (kind: string, name: string, data?: unknown) =>
      api.post<void>(base, { kind, name, data: data ?? {} }),
    update: (resId: string, data?: unknown, name?: string) =>
      api.put<void>(one(resId), {
        ...(name !== undefined ? { name } : {}),
        ...(data !== undefined ? { data } : {}),
      }),
    delete: (resId: string) => api.delete<void>(one(resId)),
    addTodo: (resId: string, text: string) =>
      api.post<void>(`${one(resId)}/todos`, { text }),
    setTodoCompleted: (resId: string, itemId: string, completed: boolean) =>
      api.patch<void>(`${one(resId)}/todos/${itemId}`, { completed }),
    removeTodo: (resId: string, itemId: string) =>
      api.delete<void>(`${one(resId)}/todos/${itemId}`),
  }
}

/**
 * List registered resource kinds with display metadata, config schema, and
 * creation scopes. The backend `ResourceHandler` registry is the single
 * source of truth for kind metadata.
 */
export function listResourceKinds() {
  return api.get<ResourceKindInfo[]>('/api/resource-kinds')
}

/** Global by-id fetch (scope-agnostic; authz resolves the owning
 * workspace server-side) — the `/r/:id` deep-link target. */
export function getResourceById(resId: string): Promise<TopicResource> {
  return api.get<TopicResource>(`/api/resources/${resId}`)
}

/** Promote a resource upward in scope (thread→topic, thread→workspace,
 * topic→workspace), preserving identity. Rejects with 409 on a name/kind
 * collision at the target scope — rename first, then retry. */
export function promoteResource(
  topicId: string,
  resId: string,
  to: 'topic' | 'workspace',
): Promise<TopicResource> {
  return api.post<TopicResource>(
    `/api/topics/${topicId}/resources/${resId}/promote`,
    { to },
  )
}
